import { Component, Input, NgZone, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import * as am5hierarchy from '@amcharts/amcharts5/hierarchy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import * as am5exporting from '@amcharts/amcharts5/plugins/exporting';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-simple-treemap-chart',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule],
  template: `
    <div [id]="chartId" style="width: 100%; height: 400px;"></div>
    <div class="d-flex justify-content-end align-items-center mb-4">
      <button mat-raised-button color="primary" (click)="exportAsImage()" aria-label="Export chart button">
        <mat-icon class="m-0">download</mat-icon>
      </button>
    </div>
  `,
})
export class SimpleTreemapChartComponent implements OnChanges, OnDestroy {
  chartId = `treemapChart-${Math.random().toString(36).substr(2, 9)}`;

  @Input() data: { authorName?: string; clipsCount?: number }[] = [];

  private root!: am5.Root;
  private exporting!: am5exporting.Exporting;

  constructor(private zone: NgZone) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.data?.length > 0) {
      this.renderChart();
    }
  }

  private renderChart(): void {
    this.disposeChart();

    this.zone.runOutsideAngular(() => {
      const root = am5.Root.new(this.chartId);
      this.root = root;

      root.setThemes([am5themes_Animated.new(root)]);

      const container = root.container.children.push(
        am5.Container.new(root, {
          width: am5.percent(100),
          height: am5.percent(100),
          layout: root.verticalLayout,
        })
      );

      const series = container.children.push(
        am5hierarchy.Treemap.new(root, {
          singleBranchOnly: false,
          downDepth: 1,
          upDepth: -1,
          initialDepth: 2,
          valueField: 'value',
          categoryField: 'name',
          childDataField: 'children',
          nodePaddingOuter: 0,
          nodePaddingInner: 0,
        })
      );

      series.rectangles.template.setAll({ strokeWidth: 2 });

      const formattedData = {
        name: 'Authors',
        children: this.data.map(item => ({
          name: item.authorName,
          value: item.clipsCount,
        })),
      };

      series.data.setAll([formattedData]);
      series.set('selectedDataItem', series.dataItems[0]);
      series.appear(1000, 100);

      this.exporting = am5exporting.Exporting.new(root, {
        pngOptions: {
          quality: 0.9,
          maintainPixelRatio: true,
        },
      });
    });
  }

  exportAsImage(): void {
    this.exporting?.download('png');
  }

  ngOnDestroy(): void {
    this.disposeChart();
  }

  private disposeChart(): void {
    if (this.root) {
      this.root.dispose();
    }
  }
}
